import { productModificationApi } from 'apiConnector/product-modification';
import type { ProductModificationFormResult } from 'models/forms/productModification';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeoutWith } from 'rxjs/operators';
import * as actionTypes from 'state/productModificationForm/productModificationForm.actionTypes';
import {
  productModificationFormRequestFailedThunk,
  productModificationFormRequestTimedOutThunk,
  productModificationFormResultReceivedThunk,
} from 'state/productModificationForm/productModificationForm.thunks';
import type { EpicWithThunk } from 'state/state.common';
import type { WorkspaceTabRemoved } from 'state/workspaces/workspace.actions';

export interface FetchProductModificationApi {
  fetchFormValues(tabId: string, productId: string): Observable<ProductModificationFormResult>;
}

export function fetchProductModificationFormEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, _state$, { oldHttpClient }) => {
    const ProductModificationFormRequested$ = action$.pipe(
      ofType(actionTypes.PRODUCT_MODIFICATION_FORM_REQUESTED, actionTypes.PRODUCT_MODIFICATION_RELOAD_FORM_REQUESTED),
    );

    return ProductModificationFormRequested$.pipe(
      mergeMap(({ tabId, productId, type }) => {
        const tabRemoved$ = action$.pipe(
          ofType('WORKSPACE_TAB_REMOVED'),
          filter((action: WorkspaceTabRemoved) => action.id === tabId),
        );

        const isReloading = type === actionTypes.PRODUCT_MODIFICATION_RELOAD_FORM_REQUESTED;

        return productModificationApi(oldHttpClient)
          .fetchFormValues(tabId, productId)
          .pipe(
            takeUntil(tabRemoved$),
            mergeMap(productFormResult =>
              of(productModificationFormResultReceivedThunk(tabId, productId, productFormResult, isReloading)),
            ),
            catchError(error =>
              of(
                productModificationFormRequestFailedThunk(
                  tabId,
                  productId,
                  error.message || 'default form failed',
                  error.traceId,
                ),
              ),
            ),
            timeoutWith(
              timeoutInMillis,
              of(productModificationFormRequestTimedOutThunk(tabId, productId)),
              appScheduler,
            ),
          );
      }),
    );
  };
}
